import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  sectionMargins,
  minBreakpointQuery,
  maxBreakpointQuery,
} from '../styles';
import { Container, HtmlContent, Svg, SectionHeader } from './ui';
import MapLocation from './MapLocation';
import mapPin from '../images/map-pin.inline.svg';
import ukMap from '../images/ecotalk-map.png';

const StyledLocationMap = styled.section`
  ${sectionMargins()};
`;

const StyledContent = styled(HtmlContent)`
  margin-top: 20px;
`;

const StyledMapWrapper = styled.div`
  position: relative;
  display: grid;
  gap: 30px;

  ${maxBreakpointQuery.smedium`
    justify-items: center;
  `}
`;

const StyledLocationWrapper = styled.div`
  ${maxBreakpointQuery.smedium`
    position: relative;
  `}
`;

const StyledMapImage = styled.img`
  width: 100%;
  max-height: 1530px;
  margin-top: 45px;
`;

const StyledMapLocation = styled(MapLocation)`
  position: absolute;
`;

const StyledPin = styled.div`
  position: absolute;
  left: ${({ $lat }) => `${$lat}%`};
  top: ${({ $lng }) => `${$lng}%`};
  transform: translate(-50%, -50%);

  &:hover {
    ${StyledMapLocation} {
      opacity: 1;
      pointer-events: auto;
    }
  }

  ${({ $isFirst }) => {
    if ($isFirst) {
      return css`
        &:before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 200px;
          height: 1px;
          background-color: black;
          transform-origin: 0 50%;
          transform: rotate(120deg);
          z-index: -1;

          ${minBreakpointQuery.smedium`
            width: 500px;
            transform: rotate(-65deg);
          `}

          ${minBreakpointQuery.large`
            width: 800px;
            transform: rotate(-68deg);
          `}
        }
      `;
    }
  }}
`;

const StyledSvg = styled(Svg)`
  margin: 20px;
  height: 20px;
  width: 20px;

  ${minBreakpointQuery.smedium`
     height: 25px;
     width: 25px;
  `}

  ${minBreakpointQuery.large`
     height: 30px;
     width: 30px;
  `}
`;

const StyledPrimaryMapLocation = styled(MapLocation)`
  ${minBreakpointQuery.smedium`
    position: absolute;
  `}
`;

const LocationMap = ({ heading, text, content }) => {
  const {
    contentfulMapLocationsModularBlock: {
      primaryLocationName,
      primaryLocationText,
      primaryLocationImage,
      primaryLocationLink,
      secondaryLocationOneName,
      secondaryLocationOneText,
      secondaryLocationOneImage,
      secondaryLocationTwoName,
      secondaryLocationTwoText,
      secondaryLocationTwoImage,
    },
  } = useStaticQuery(graphql`
    query MapQuery {
      contentfulMapLocationsModularBlock {
        primaryLocationName
        primaryLocationText
        primaryLocationImage {
          gatsbyImageData
          title
        }
        primaryLocationLink {
          pageUrl {
            ...LinkFragment
          }
        }
        secondaryLocationOneName
        secondaryLocationOneText
        secondaryLocationOneImage {
          gatsbyImageData
          title
        }
        secondaryLocationTwoName
        secondaryLocationTwoText
        secondaryLocationTwoImage {
          gatsbyImageData
          title
        }
      }
    }
  `);

  const calculateCSSValues = (x, y) => {
    const leftPercentage = ((x - -9.1) / (2.7 - -9.1)) * 100;
    const topPercentage = ((y - 59.9) / (49.9 - 59.9)) * 100;
    return {
      left: leftPercentage.toFixed(2),
      top: topPercentage.toFixed(2),
    };
  };

  const array = [
    {
      latitude: 50.6,
      longitude: -0.9,
      image: primaryLocationImage,
      name: primaryLocationName,
      siteLocation: 'Sussex',
      acres: 257,
      content: primaryLocationText,
      link: primaryLocationLink,
    },
    {
      latitude: 51.9,
      longitude: -2.5,
      image: secondaryLocationOneImage,
      name: secondaryLocationOneName,
      content: secondaryLocationOneText,
    },
    {
      latitude: 52.7,
      longitude: -3.8,
      image: secondaryLocationTwoImage,
      name: secondaryLocationTwoName,
      content: secondaryLocationTwoText,
    },
  ];

  return (
    <StyledLocationMap>
      <Container>
        <SectionHeader heading={heading} text={text} />
        <StyledContent content={content} />
        <StyledMapWrapper>
          <StyledLocationWrapper>
            <StyledMapImage src={ukMap} alt="Map of the UK" loading="lazy" />
            {array.map(({ latitude, longitude, image, name, content }, id) => (
              <StyledPin
                key={id}
                $lat={calculateCSSValues(longitude, latitude).left}
                $lng={calculateCSSValues(longitude, latitude).top}
                $isFirst={id === 0}
              >
                <StyledSvg image={mapPin} />
                {id !== 0 && (
                  <StyledMapLocation
                    image={image}
                    name={name}
                    content={content}
                  />
                )}
              </StyledPin>
            ))}
          </StyledLocationWrapper>
          <StyledPrimaryMapLocation
            image={array[0].image}
            name={array[0].name}
            location={array[0].siteLocation}
            acres={array[0].acres}
            content={array[0].content}
            link={array[0].link}
            isLarge={true}
          />
        </StyledMapWrapper>
      </Container>
    </StyledLocationMap>
  );
};

export default LocationMap;
